/**
 * Select Options — custom variant picker for Shopify Dawn
 *
 * Responsibilities:
 * 1. When a card is clicked, select the matching product variant using
 *    Dawn's own <variant-selects> / <variant-radios> components (so price,
 *    availability, media, the hidden "id" input and Add to Cart all update
 *    exactly the way Dawn already handles it) — or, for single-variant
 *    products with no native picker, update the hidden id input directly.
 * 2. When the variant changes from elsewhere on the page (native swatches,
 *    dropdowns, or a deep link with a variant in the URL), reflect the
 *    correct card as selected here too.
 */
(function () {
  'use strict';

  var wrappers = document.querySelectorAll('.select-options-wrapper');
  if (!wrappers.length) return;

  wrappers.forEach(initSelectOptions);

  function initSelectOptions(wrapper) {
    var track = wrapper.querySelector('.select-options__track');
    if (!track) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('.select-options__card'));
    if (!cards.length) return;

    setupCarouselArrows(wrapper, track);

    // Find the product form / container this section belongs to. Since this
    // is a standalone section dropped onto a product template, we look for
    // the closest product form on the page.
    var productForm = findProductForm();
    var nativePicker = findNativeVariantPicker(productForm);
    var hiddenIdInput = findHiddenIdInput(productForm);

    // ---- Card click: select a variant --------------------------------
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        selectVariantFromCard(card);
      });
    });

    function selectVariantFromCard(card) {
      var variantId = card.getAttribute('data-variant-id');
      if (!variantId) return;

      if (nativePicker) {
        var applied = applyVariantToNativePicker(nativePicker, card);
        if (applied) {
          // Dawn's own change handler takes it from here (price, media,
          // availability, hidden id input, section re-render). We still
          // mark this card selected immediately for a snappy UI, and the
          // sync-from-native listener below will confirm/correct it.
          markSelected(card);
          return;
        }
      }

      // Fallback: no native option-based picker found (e.g. a
      // single-variant product), so update the hidden id input directly.
      if (hiddenIdInput) {
        hiddenIdInput.value = variantId;
        hiddenIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      markSelected(card);
    }

    function markSelected(selectedCard) {
      cards.forEach(function (card) {
        var isSelected = card === selectedCard;
        card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
    }

    // ---- Sync FROM native picker / hidden input TO cards --------------
    function syncFromCurrentVariantId() {
      var currentId = hiddenIdInput ? hiddenIdInput.value : null;
      if (!currentId) return;
      var match = cards.filter(function (card) {
        return card.getAttribute('data-variant-id') === String(currentId);
      })[0];
      if (match) {
        markSelected(match);
      } else {
        cards.forEach(function (card) {
          card.setAttribute('aria-checked', 'false');
        });
      }
    }

    // Run once on load in case a variant is already selected (e.g. via URL
    // query string) so a card starts highlighted.
    syncFromCurrentVariantId();

    // Listen for native variant changes anywhere on the page (dropdowns,
    // swatches, radios) and re-sync our cards afterwards. We use document
    // level delegation so this works regardless of Dawn's internal markup,
    // and defer via requestAnimationFrame so Dawn's own handlers (which
    // update the hidden id input) run first.
    document.addEventListener('change', function (event) {
      if (!isInsideNativePicker(event.target)) return;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(syncFromCurrentVariantId);
      });
    });

    // Dawn also updates variant state after fetching updated section HTML,
    // which can replace the hidden input node. Re-resolve it lazily.
    function findHiddenIdInputLive() {
      hiddenIdInput = findHiddenIdInput(productForm) || hiddenIdInput;
      return hiddenIdInput;
    }

    // Re-check periodically-safe: observe DOM mutations on the product form
    // so a re-rendered form still keeps this section in sync.
    if (productForm && 'MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        findHiddenIdInputLive();
        syncFromCurrentVariantId();
      });
      observer.observe(productForm, { childList: true, subtree: true });
    }
  }

  // -----------------------------------------------------------------
  // Carousel arrows — wraps the track in a positioned "viewport" div and
  // injects prev/next buttons, shown only while there is more content to
  // scroll to in that direction. Pure DOM/CSS, no markup change needed.
  // -----------------------------------------------------------------

  function setupCarouselArrows(wrapper, track) {
    if (track.parentNode.classList && track.parentNode.classList.contains('select-options__viewport')) {
      return; // already set up (e.g. re-init)
    }

    var viewport = document.createElement('div');
    viewport.className = 'select-options__viewport';
    track.parentNode.insertBefore(viewport, track);
    viewport.appendChild(track);

    var prevBtn = createArrowButton('prev', 'Scroll to previous options');
    var nextBtn = createArrowButton('next', 'Scroll to more options');
    viewport.appendChild(prevBtn);
    viewport.appendChild(nextBtn);

    function cardStep() {
      var card = track.querySelector('.select-options__card');
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -cardStep(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: cardStep(), behavior: 'smooth' });
    });

    function updateArrowVisibility() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var hasOverflow = maxScroll > 4;
      if (!hasOverflow) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
        return;
      }
      prevBtn.hidden = track.scrollLeft <= 4;
      nextBtn.hidden = track.scrollLeft >= maxScroll - 4;
    }

    track.addEventListener('scroll', updateArrowVisibility, { passive: true });
    window.addEventListener('resize', updateArrowVisibility);
    updateArrowVisibility();

    // Layout can settle a tick after load (fonts/images), recheck once more.
    window.setTimeout(updateArrowVisibility, 300);
  }

  function createArrowButton(direction, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'select-options__arrow select-options__arrow--' + direction;
    btn.setAttribute('aria-label', label);
    btn.hidden = true;
    var pathD = direction === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + pathD + '" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return btn;
  }

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------

  function findProductForm() {
    // Dawn wraps the add-to-cart form in a <product-form> custom element.
    var productFormEl = document.querySelector('product-form');
    if (productFormEl) {
      var form = productFormEl.querySelector('form');
      if (form) return form;
    }
    // Fallback: any form that posts to the cart.
    return document.querySelector('form[action*="/cart/add"]');
  }

  function findNativeVariantPicker(productForm) {
    var picker = document.querySelector('variant-selects, variant-radios');
    if (picker) return picker;
    if (productForm) {
      var root = productForm.closest('.product, .product-info, main') || document;
      return root.querySelector('variant-selects, variant-radios');
    }
    return null;
  }

  function findHiddenIdInput(productForm) {
    if (productForm) {
      var input = productForm.querySelector('input[name="id"]');
      if (input) return input;
    }
    return document.querySelector('input[name="id"]');
  }

  function isInsideNativePicker(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('variant-selects, variant-radios');
  }

  /**
   * Sets the native <select> elements or checked radio inputs inside Dawn's
   * variant picker to match the option1/2/3 values of the variant behind
   * `card`, then dispatches a 'change' event so Dawn's own listeners run
   * (updating price, media, availability, URL and the hidden id input).
   * Returns true if at least one control was updated.
   */
  function applyVariantToNativePicker(picker, card) {
    var options = [
      card.getAttribute('data-option1'),
      card.getAttribute('data-option2'),
      card.getAttribute('data-option3'),
    ].filter(function (value) {
      return value !== null && value !== '';
    });

    if (!options.length) return false;

    var changedAnything = false;
    var lastChangedControl = null;

    // Dropdown style: one <select> per option position, each containing
    // <option value="...">Option value</option>.
    var selects = picker.querySelectorAll('select');
    if (selects.length) {
      selects.forEach(function (select, index) {
        var targetValue = options[index];
        if (targetValue === undefined) return;
        var optionEl = Array.prototype.slice
          .call(select.options)
          .filter(function (opt) {
            return opt.value === targetValue || opt.textContent.trim() === targetValue;
          })[0];
        if (optionEl && select.value !== optionEl.value) {
          select.value = optionEl.value;
          changedAnything = true;
          lastChangedControl = select;
        } else if (optionEl) {
          lastChangedControl = lastChangedControl || select;
        }
      });
    }

    // Radio/swatch style: fieldsets of radio inputs, one fieldset per
    // option position, each radio's value being the option value.
    var fieldsets = picker.querySelectorAll('fieldset');
    if (fieldsets.length) {
      fieldsets.forEach(function (fieldset, index) {
        var targetValue = options[index];
        if (targetValue === undefined) return;
        var radios = fieldset.querySelectorAll('input[type="radio"]');
        radios.forEach(function (radio) {
          var matches = radio.value === targetValue;
          if (matches && !radio.checked) {
            radio.checked = true;
            changedAnything = true;
            lastChangedControl = radio;
          } else if (matches) {
            lastChangedControl = lastChangedControl || radio;
          }
        });
      });
    }

    if (lastChangedControl) {
      // Always dispatch, even if the value already matched, so Dawn
      // re-confirms state (harmless / idempotent) the first time a card is
      // clicked when the native picker already agreed with it.
      lastChangedControl.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return changedAnything;
  }
})();